import type { CardStatus, LimitRenewFrequency, EmployeeCard } from './api';
import {
  addDays,
  addMonths,
  addQuarters,
  addYears,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  nextMonday,
} from 'date-fns';

/**
 * Check if card status is terminated
 * @param status - Card status
 * @returns True if card is terminated
 * @example
 * isCardTerminated('TERMINATED') => true
 * isCardTerminated('TERMINATED_LOST') => true
 * isCardTerminated('ACTIVE') => false
 */
export const isCardTerminated = (status: CardStatus): boolean => {
  return (
    status === 'TERMINATED' ||
    status === 'TERMINATED_LOST' ||
    status === 'TERMINATED_STOLEN'
  );
};

/**
 * Get color for card status badge
 * @param status - Card status
 * @returns Color variant
 */
export const getStatusColor = (
  status: CardStatus
): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'LOCKED':
    case 'LOCKED_PIN':
    case 'PENDING_ACTIVATION':
    case 'PENDING_ISSUANCE':
    case 'PENDING_REGISTRATION':
    case 'PENDING_VALIDITY':
      return 'warning';
    case 'TERMINATED':
    case 'TERMINATED_LOST':
    case 'TERMINATED_STOLEN':
    case 'EXPIRED':
    case 'REQUEST_REJECTED':
    case 'REQUEST_CANCELLED':
      return 'error';
    case 'PENDING':
    case 'REQUEST_APPROVED':
    case 'REQUESTED':
      return 'info';
    default:
      return 'default';
  }
};

/**
 * Format limit renew frequency for display
 * @param frequency - Limit renew frequency
 * @returns Localized string key
 */
export const formatRenewFrequency = (
  frequency: LimitRenewFrequency
): string => {
  const frequencyMap: Record<LimitRenewFrequency, string> = {
    DAILY: 'myCards.frequency.daily',
    WEEKLY: 'myCards.frequency.weekly',
    MONTHLY: 'myCards.frequency.monthly',
    QUARTERLY: 'myCards.frequency.quarterly',
    ANNUAL: 'myCards.frequency.annual',
    TOTAL: 'myCards.frequency.total',
  };
  return frequencyMap[frequency];
};

/**
 * Get contextual limit label based on renewal frequency
 * @param frequency - Limit renew frequency
 * @returns Translation key for the contextual limit label
 * @example
 * getContextualLimitLabel('MONTHLY') => 'myCards.monthlyLimit'
 * getContextualLimitLabel('DAILY') => 'myCards.dailyLimit'
 */
export const getContextualLimitLabel = (
  frequency: LimitRenewFrequency | null
): string => {
  if (!frequency) {
    return 'myCards.monthlyLimit';
  }

  const labelMap: Record<LimitRenewFrequency, string> = {
    DAILY: 'myCards.dailyLimit',
    WEEKLY: 'myCards.weeklyLimit',
    MONTHLY: 'myCards.monthlyLimit',
    QUARTERLY: 'myCards.quarterlyLimit',
    ANNUAL: 'myCards.annualLimit',
    TOTAL: 'myCards.totalLimit',
  };
  return labelMap[frequency];
};

/**
 * Calculate effective cash withdrawal limits considering card's total limit
 * The effective limit is the minimum of the card's total limit and the general withdrawal limit
 *
 * @param card - Employee card with limits
 * @returns Effective withdrawal limits
 * @example
 * // If card total_limit is 50000 (€500) and max_per_transaction is 100000 (€1000)
 * // Effective max_per_transaction becomes 50000 (€500)
 */
export const getEffectiveCashWithdrawalLimits = (
  card: Pick<EmployeeCard, 'total_limit' | 'cash_withdrawal_limits'>
): EmployeeCard['cash_withdrawal_limits'] => {
  const { total_limit, cash_withdrawal_limits } = card;

  return {
    max_per_transaction: Math.min(
      cash_withdrawal_limits.max_per_transaction,
      total_limit
    ),
    min_amount: cash_withdrawal_limits.min_amount,
    daily_limit: Math.min(cash_withdrawal_limits.daily_limit, total_limit),
    four_day_limit: Math.min(
      cash_withdrawal_limits.four_day_limit,
      total_limit
    ),
    daily_frequency: cash_withdrawal_limits.daily_frequency,
    four_day_frequency: cash_withdrawal_limits.four_day_frequency,
  };
};

/**
 * Calculate next reset date for card limit renewal based on frequency
 * DAILY resets tomorrow, WEEKLY on next Monday, MONTHLY on 1st of next month,
 * QUARTERLY on 1st of next quarter, ANNUAL on next Jan 1st
 *
 * @param frequency - How often the limit renews
 * @returns Next future reset date, or null if no frequency or TOTAL
 * @example
 * // Today is Nov 2, 2025, DAILY returns Nov 3, 2025
 * // Today is Nov 2, 2025, MONTHLY returns Dec 1, 2025
 * getNextResetDate('MONTHLY')
 */
export const getNextResetDate = (
  frequency: LimitRenewFrequency | null
): Date | null => {
  if (!frequency) {
    return null;
  }

  // Use UTC midnight since Pliant resets limits at UTC midnight
  const now = new Date();
  const today = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  );

  switch (frequency) {
    case 'TOTAL':
      // No recurring reset
      return null;

    case 'DAILY':
      // Resets tomorrow at midnight
      return startOfDay(addDays(today, 1));

    case 'WEEKLY':
      // Resets next Monday at midnight
      return nextMonday(today);

    case 'MONTHLY':
      // Resets 1st of next month at midnight
      return startOfMonth(addMonths(today, 1));

    case 'QUARTERLY':
      // Resets 1st of next quarter at midnight (Jan 1, Apr 1, Jul 1, Oct 1)
      return startOfQuarter(addQuarters(today, 1));

    case 'ANNUAL':
      // Resets next Jan 1st at midnight
      return startOfYear(addYears(today, 1));

    default:
      return null;
  }
};
